import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FaGithub, 
  FaLinkedin, 
  FaFacebook, 
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaCode,
  FaHeart
} from 'react-icons/fa';
import { HiOutlineMail } from 'react-icons/hi';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Projects', path: '/projects' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    'Web Development',
    'UI/UX Design',
    'PHP Development',
    'MERN Stack',
    'Database Design',
  ];

  const socialLinks = [
    { 
      icon: FaGithub, 
      href: 'https://github.com/sheru-project', 
      label: 'GitHub', 
      color: '#333',
      hoverColor: '#000'
    },
    { 
      icon: FaLinkedin, 
      href: 'https://www.linkedin.com/in/sarfrazahmed1107/', 
      label: 'LinkedIn', 
      color: '#0077B5',
      hoverColor: '#005E93'
    },
    { 
      icon: FaFacebook, 
      href: 'https://www.facebook.com/sherubhai1107', 
      label: 'Facebook', 
      color: '#1877F2',
      hoverColor: '#0D65D9'
    },
    { 
      icon: HiOutlineMail, 
      href: 'mailto:listenrhymes@gmail.com', 
      label: 'Email', 
      color: '#EA4335',
      hoverColor: '#C5221F'
    },
  ];

  return (
    <footer className="bg-white dark:bg-gray-950 border-t border-gray-200 dark:border-gray-800 pt-16 pb-8">
      <div className="container-custom px-4">
        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* About Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-4"
          >
            <h3 className="text-2xl font-display font-bold">
              <span className="text-primary">Sarfraz</span>
              <span className="text-gray-800 dark:text-gray-100"> Ahmed</span>
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
              Building scalable, secure, and performant web applications with 
              MERN Stack, PHP/MySQL, and modern technologies.
            </p>
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
              <FaCode className="text-primary" />
              <span>Full Stack Developer</span>
            </div>
            <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-300">
              <FaHeart className="text-secondary" />
              <span>Available for Work</span>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-4"
          >
            <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-gray-500 dark:text-gray-400 hover:text-primary transition-colors duration-300 text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Services */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-4"
          >
            <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Services</h4>
            <ul className="space-y-2">
              {services.map((service, index) => (
                <li key={index}>
                  <span className="text-gray-500 dark:text-gray-400 hover:text-primary transition-colors duration-300 text-sm cursor-pointer">
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="space-y-4"
          >
            <h4 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Get in Touch</h4>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <FaMapMarkerAlt className="text-primary text-lg mt-1" />
                <span className="text-gray-600 dark:text-gray-300 text-sm">Pakistan</span>
              </div>
              <div className="flex items-start space-x-3">
                <FaPhone className="text-primary text-lg mt-1" />
                <span className="text-gray-600 dark:text-gray-300 text-sm">+92 303 3365593</span>
              </div>
              <div className="flex items-start space-x-3">
                <FaEnvelope className="text-primary text-lg mt-1" />
                <span className="text-gray-600 dark:text-gray-300 text-sm">listenrhymes@gmail.com</span>
              </div>
            </div>

            {/* Professional Social Icons */}
            <div className="flex flex-wrap gap-3 pt-2">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative w-11 h-11 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center transition-all duration-300 hover:shadow-lg"
                  whileHover={{ 
                    y: -5,
                    scale: 1.05,
                    transition: { duration: 0.2 }
                  }}
                  whileTap={{ scale: 0.95 }}
                  aria-label={social.label}
                >
                  {/* Tooltip */}
                  <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                    {social.label}
                  </span>
                  
                  <social.icon 
                    className="text-lg transition-all duration-300 group-hover:scale-110" 
                    style={{ 
                      color: social.color 
                    }}
                  />
                  
                  {/* Hover Background Effect */}
                  <div 
                    className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"
                    style={{ backgroundColor: social.color }}
                  />
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="border-t border-gray-200 dark:border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0"
        >
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            © {currentYear} <span className="text-primary font-medium">Sarfraz Ahmed</span>. All rights reserved.
          </p>
          <p className="text-gray-500 dark:text-gray-400 text-sm flex items-center gap-2">
            Made with <FaHeart className="text-secondary animate-pulse" /> using React & Tailwind CSS
          </p>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;